import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCrosshair } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { PLACEMENT_EDIT_MODE, buildEditUrl, buildPreviewUrl, withPreviewMode } from '../helpers/previewSdk';
import useFetch from '../helpers/useFetch';
import { PLACEMENT_RETURN_PARAM } from './useRestorePlacementSession';

// Where the shop page sends the merchant back to: this same admin route, named
// as the session it is returning from. The admin is a hash router, so the route
// is the fragment of this tab's address — the rest of it (the origin, the path
// the admin is served from, any query it carries) is left exactly as it is.
const returnUrl = (location, token) => {
  const params = new URLSearchParams(location.search);
  params.set(PLACEMENT_RETURN_PARAM, token);

  const here = new URL(window.location.href);
  here.hash = `#${location.pathname}?${params.toString()}`;
  return here.href;
};

// Picks where this block goes on the customer's real site: this tab goes to a
// page of their shop, they drop the block on an element there, and the page's
// Done button brings them back here with the position applied. Nothing is
// framed, so it works whatever the shop's x-frame-options says.
//
// Used by both kinds of page block that a CSS selector positions — product
// blocks and static banner blocks — which is what `blocksPath` and `blockLabel`
// are for: the collection segment of the block's placement-session routes
// (`recommenders` or `banner-blocks`) and the name to call it in the copy. One
// implementation rather than one per block type, because the rules below about
// what is stored and when are the same rules for both, and the trip loses a
// merchant's unsaved work wherever they are got wrong.
//
// The form is not saved when they leave, so the whole in-progress block goes
// onto the session on the way out and comes back off it on the way in — going
// to look at the shop must not cost a merchant a rename or a retitled block.
// The reported position is laid over the restored form and left there: saving
// stays their own action, exactly as when they type a selector in by hand.
//
// Only the outbound half of the trip lives here. Coming back is
// `useRestorePlacementSession`, in the page that owns the route: this control
// is not guaranteed to be mounted on the way back in (a display-type change
// made since the trip started leaves it out of the tree), and by the time it
// could be, the block the form opens on has already been decided.
//
// `children` is the CSS selector field this control fills in. It is rendered
// here, on the button's own row, so the two read as one control; the error
// notice stays outside that row, at the field's full width.
const PositionOnMySite = ({ block, blocksPath, blockLabel, children }) => {
  const { domain } = useSelector((state) => state.domain);
  const fetch = useFetch();
  const location = useLocation();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const domainPath = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;
  const sessionsPath = `${domainPath}/pages/${block.pageId}/${blocksPath}/${block.id}/placement-sessions`;

  // `starting` is deliberately left set once the tab starts leaving (see
  // `start` below), but pressing Back on the shop page restores this document
  // from the back/forward cache with JS state intact rather than reloading
  // it, so nothing else would ever clear it in that case. `rlv_placement`
  // is not on the route then either, so the busy button would otherwise be
  // stuck until a manual reload.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setStarting(false);
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const start = async () => {
    setError(null);
    setStarting(true);

    try {
      // A page's pathname can be a pattern rather than a reachable url, so ask
      // for the most recent url actually seen on it; when there is none (or the
      // lookup fails) the pathname against the domain is the best guess left.
      const { url } = await fetch(`${domainPath}/pages/${block.pageId}/recent-url`).catch(() => ({}));

      const openUrl =
        withPreviewMode(url, PLACEMENT_EDIT_MODE) ||
        buildPreviewUrl(domain.url, block.page?.pathname, PLACEMENT_EDIT_MODE);

      if (!openUrl) {
        throw new Error('There is no web address we can open for this page.');
      }

      const { token } = await fetch(sessionsPath, { method: 'POST' });

      // The whole in-progress block, not just the selector: this tab is about
      // to leave, and everything changed since the last Save exists nowhere but
      // the form until it is on the session.
      await fetch(`${sessionsPath}/${token}/state`, {
        method: 'PUT',
        body: JSON.stringify({ state: block }),
      });

      // Same tab, straight from the click, so there is no popup to be blocked
      // and no second window to disown. `starting` is deliberately left set —
      // the button stays busy until the browser leaves this page.
      window.location.assign(
        buildEditUrl(openUrl, token, { mode: PLACEMENT_EDIT_MODE, returnUrl: returnUrl(location, token) })
      );
    } catch (e) {
      setError(e.message || 'Could not start a session on your site.');
      setStarting(false);
    }
  };

  // The session path is built from the block's id, and a block that has never
  // been saved does not have one yet.
  if (!block.id) {
    return (
      <>
        <Text size="xs" c="dimmed" mb="xs">
          Save this {blockLabel} first, and you can then position it on your own site.
        </Text>
        {children}
      </>
    );
  }

  return (
    <Stack gap="xs">
      {/* Top-aligned: the field grows a line taller when it has an error, and
          the button belongs against the input, not against that message. */}
      <Group gap="xs" wrap="nowrap" align="flex-start">
        {children}
        <Button variant="default" loading={starting} onClick={start} leftSection={<IconCrosshair size={16} />}>
          Position on my site
        </Button>
      </Group>

      {error && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">{error}</Text>
        </Alert>
      )}
    </Stack>
  );
};

export default PositionOnMySite;
